import { authClient } from './amplifyClient';
import {
  createCompany as createCompanyMutation,
  updateCompanyOwner as updateCompanyOwnerMutation,
} from '../graphql/mutations';
import { Company, CompanyOwner, CreateCompanyInput } from '../types/schema';

/** Free-demo limits — matches CompanyHomepage trial defaults. */
export const FREE_DEMO_MAX_USERS = 4;
export const FREE_DEMO_MAX_EQUIPMENT = 4;
export const FREE_DEMO_TRIAL_DAYS = 30;

function addDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
}

export type CreateCompanyForOwnerParams = {
  companyOwner: CompanyOwner;
  name: string;
};

/**
 * Creates a Company for an existing CompanyOwner and links
 * CompanyOwner.companyId → Company.id (and Company.companyOwnerId).
 */
export async function createCompanyForOwner({
  companyOwner,
  name,
}: CreateCompanyForOwnerParams): Promise<Company> {
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Company name is required');
  }
  if (!companyOwner.id) {
    throw new Error('CompanyOwner id is required');
  }

  const subscriptionType = companyOwner.subscriptionType || 'free-demo';
  const isFreeDemo = subscriptionType === 'free-demo';
  const trialEnd = addDaysIso(FREE_DEMO_TRIAL_DAYS);
  const today = new Date().toISOString();

  const input: CreateCompanyInput = {
    name: trimmedName,
    companyOwnerId: companyOwner.id,
    subscriptionType,
    subscriptionExpiration: trialEnd,
    maxNumberUsers: isFreeDemo ? FREE_DEMO_MAX_USERS : 10,
    maxNumberEquipment: isFreeDemo ? FREE_DEMO_MAX_EQUIPMENT : 10,
    accountOrigin: 'self-signup',
    accountStatus: isFreeDemo ? 'trial' : 'active',
    trialStartDate: isFreeDemo ? today : undefined,
    trialEndDate: isFreeDemo ? trialEnd : undefined,
    initialStarterPackageEnrollment: true,
    trainingModuleEnrollment: isFreeDemo,
    pmModuleEnrollment: isFreeDemo,
    tiresModuleEnrollment: isFreeDemo,
    skillTrackingModuleEnrollment: isFreeDemo,
    costTrackingModuleEnrollment: isFreeDemo,
  };

  const createResponse = await authClient.graphql({
    query: createCompanyMutation,
    variables: { input },
  });

  const created = createResponse.data?.createCompany as Company | null | undefined;
  if (!created?.id) {
    throw new Error('createCompany returned no id');
  }

  await authClient.graphql({
    query: updateCompanyOwnerMutation,
    variables: {
      input: {
        id: companyOwner.id,
        companyId: created.id,
      },
    },
  });

  return created;
}
